import { collection, addDoc, query, where, orderBy, getDocs, getDoc, doc } from 'firebase/firestore';
import { firestore } from '../firebase/config';

// Generate a unique invoice number
export const generateInvoiceNumber = () => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000);
  return `INV-${timestamp}-${random}`;
};

// Generate invoice for pending fee automatically
export const generateInvoiceForPendingFee = async (studentData, pendingAmount, showSuccess, showError) => {
  try {
    
    const invoiceData = {
      type: 'Fee',
      name: studentData.fullName,
      role: 'Student',
      amount: parseFloat(studentData.totalFee),
      paid: parseFloat(studentData.submittedFee),
      pending: pendingAmount,
      date: new Date().toLocaleString(),
      status: pendingAmount > 0 ? 'Pending' : 'Paid',
      invoiceNo: generateInvoiceNumber(),
      studentId: studentData.uid || studentData.id, // Use uid if available, fallback to id
      autoGenerated: true,
      // Add special flag for paid invoices
      isPaidInvoice: pendingAmount === 0,
    };

    const invoicesRef = collection(firestore, 'invoices');
    const docRef = await addDoc(invoicesRef, invoiceData);
    
    const newInvoiceWithId = { ...invoiceData, id: docRef.id };
    
    if (showSuccess) {
      if (pendingAmount === 0) {
        showSuccess('Invoice generated for paid fees!');
      } else {
        showSuccess('Invoice generated automatically for pending fee!');
      }
    }
    
    return newInvoiceWithId;
  } catch (error) {
    console.error('Error generating invoice:', error);
    if (showError) {
      showError('Error generating invoice: ' + error.message);
    }
    return null;
  }
};

// Get student invoices
export const getStudentInvoices = async (studentId) => {
  try {
    // Now get invoices for this specific student
    const invoicesRef = collection(firestore, 'invoices');
    const q = query(
      invoicesRef,
      where('studentId', '==', studentId)
    );
    const snapshot = await getDocs(q);
    let invoices = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // If no invoices found by studentId, try to find by name (for older invoices without studentId)
    if (invoices.length === 0) {
      
      // Get the student name from the studentId (extract from the UID)
      // For now, we'll need to get the student data to find their name
      try {
        const studentDoc = await getDoc(doc(firestore, 'students', studentId));
        if (studentDoc.exists()) {
          const studentData = studentDoc.data();
          const studentName = studentData.fullName;
          
          // Get all invoices and filter by name client-side
          const allInvoicesQuery = query(invoicesRef);
          const allInvoicesSnapshot = await getDocs(allInvoicesQuery);
          const allInvoices = allInvoicesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Filter invoices by name (case-insensitive)
          invoices = allInvoices.filter(invoice => 
            invoice.name && 
            invoice.name.toLowerCase() === studentName.toLowerCase()
          );
        }
      } catch (error) {
        console.error('Error fetching student data for name lookup:', error);
      }
    }
    
    // Sort client-side to avoid index requirement
    invoices.sort((a, b) => new Date(b.date) - new Date(a.date));
    return invoices;
  } catch (error) {
    console.error('Error fetching student invoices:', error);
    return [];
  }
};

// Get all invoices with search and pagination
export const getInvoices = async (searchTerm = '', searchType = 'all', lastDoc = null, limitCount = 10) => {
  try {
    // Simplified query - just get all invoices and filter client-side
    const invoicesRef = collection(firestore, 'invoices');
    const q = query(invoicesRef);
    const snapshot = await getDocs(q);
    const invoiceList = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort client-side to avoid index requirement
    invoiceList.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    // Apply client-side filtering
    let filteredInvoices = invoiceList;
    
    if (searchTerm.trim()) {
      filteredInvoices = filteredInvoices.filter(invoice => 
        invoice.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.role.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (searchType !== 'all') {
      filteredInvoices = filteredInvoices.filter(invoice => 
        invoice.type.toLowerCase() === searchType.toLowerCase()
      );
    }
    
    return {
      invoices: filteredInvoices,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      hasMore: filteredInvoices.length >= limitCount
    };
  } catch (error) {
    console.error('Error fetching invoices:', error);
    return {
      invoices: [],
      lastDoc: null,
      hasMore: false
    };
  }
};
